import { exportInvoicesMonthly } from '@/api/invoices';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useDunningStatus, useInvoices, useInvoiceStats } from '@/hooks/use-invoices';
import { useServerPagination } from '@/hooks/use-server-pagination';
import { useTranslation } from '@kgc/i18n';
import { DataTable, toast } from '@kgc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DUNNING_LEVELS, INVOICE_STATUSES, INVOICE_TYPES, NAV_STATUSES } from './mock-data';
import type { Invoice, InvoiceStatus, InvoiceType } from './types';

const formatPrice = (price: number): string =>
  new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(price);

const formatDate = (dateStr: string): string => new Date(dateStr).toLocaleDateString('hu-HU');

// ============================================
// Havi export (55Y №3) — hónap-helperek
//
// A VATSummaryPage getCurrentMonth/getMonthsBack mintáját követi, de lokális
// (a VATSummaryPage page-modul, onnan nem importálható) — lásd feladat-scope.
// ============================================

const formatMonthValue = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

/** Az ELŐZŐ (lezárt) hónap YYYY-MM formában — a havi export alapértelmezett választása. */
const getPreviousMonthValue = (): string => {
  const now = new Date();
  return formatMonthValue(new Date(now.getFullYear(), now.getMonth() - 1, 1));
};

/** Az utolsó `count` hónap YYYY-MM formában, a jelenlegi hónaptól visszafelé (azt is beleértve). */
const getLastMonthValues = (count: number): string[] => {
  const now = new Date();
  const months: string[] = [];
  for (let i = 0; i < count; i++) {
    months.push(formatMonthValue(new Date(now.getFullYear(), now.getMonth() - i, 1)));
  }
  return months;
};

/** hu-HU hónap-label egy YYYY-MM értékből, pl. "2026. június". */
const formatMonthLabel = (month: string): string => {
  const [year, mon] = month.split('-').map(Number);
  return new Date(year ?? 0, (mon ?? 1) - 1, 1).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'long',
  });
};

export function InvoiceListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: '',
    status: 'ALL' as InvoiceStatus | 'ALL',
    type: 'ALL' as InvoiceType | 'ALL',
  });

  // Server-mode pagination (BE-S2 = A: a DataTable adaptál a meglévő page/pageSize/hasMore-hoz).
  // Korábban a lista CSAK a BE-default első 20 számláját mutatta (a 21+ némán láthatatlan volt).
  const serverPagination = useServerPagination(20);

  // API hooks
  const { invoices, total, isLoading, error } = useInvoices({
    search: filters.search || undefined,
    status: filters.status !== 'ALL' ? filters.status : undefined,
    type: filters.type !== 'ALL' ? filters.type : undefined,
    ...serverPagination.query,
  });

  // Szűrő-váltáskor ugorj az 1. oldalra (különben üres lehet egy szűkebb találat-halmazon).
  useEffect(() => {
    serverPagination.reset();
  }, [filters.search, filters.status, filters.type, serverPagination.reset]);

  const { stats } = useInvoiceStats();
  const { invoices: dunningInvoices } = useDunningStatus();

  // Havi export (55Y №3) — default = ELŐZŐ (lezárt) hónap, választható az utolsó 12.
  const [exportMonth, setExportMonth] = useState(getPreviousMonthValue);
  const [exportingFormat, setExportingFormat] = useState<'xlsx' | 'csv' | null>(null);
  const exportMonthOptions = useMemo(() => getLastMonthValues(12), []);

  const handleExport = async (format: 'xlsx' | 'csv') => {
    setExportingFormat(format);
    try {
      const blob = await exportInvoicesMonthly(exportMonth, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `szamla-export-${exportMonth}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('invoice.export.error');
      toast.error(message);
    } finally {
      setExportingFormat(null);
    }
  };

  // Build a map of invoiceId -> dunningLevel for quick lookup
  const dunningMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of dunningInvoices) {
      map.set(d.id, d.dunningLevel);
    }
    return map;
  }, [dunningInvoices]);

  const getDunningBadge = (invoiceId: string) => {
    const level = dunningMap.get(invoiceId);
    if (!level || level === 'NONE') return null;
    const config = DUNNING_LEVELS.find(d => d.value === level);
    if (!config) return null;
    return (
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${config.color}`}>
        {t(config.labelKey)}
      </span>
    );
  };

  const getStatusBadge = (status: InvoiceStatus) => {
    const config = INVOICE_STATUSES.find(s => s.value === status);
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : status}
      </span>
    );
  };

  const getTypeBadge = (type: InvoiceType) => {
    const config = INVOICE_TYPES.find(tp => tp.value === type);
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : type}
      </span>
    );
  };

  const getNavStatusBadge = (navStatus?: string) => {
    if (!navStatus) return null;
    const config = NAV_STATUSES.find(s => s.value === navStatus.toUpperCase());
    return (
      <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : navStatus}
      </span>
    );
  };

  // Column definitions a megosztott DataTable-höz (a cella-renderek megőrzik az eredeti markup-ot).
  const columns: ColumnDef<Invoice>[] = [
    {
      id: 'invoiceNumber',
      header: t('invoicePage.invoiceNumber'),
      cell: ({ row }) => {
        const invoice = row.original;
        return (
          <>
            <span className="font-mono font-medium text-gray-900 dark:text-gray-100">
              {invoice.invoiceNumber}
            </span>
            {invoice.eInvoiceSentAt && (
              <span
                className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                title={t('invoice.eInvoice.sentAt')}
              >
                E
              </span>
            )}
            {invoice.voidedInvoiceId && (
              <span className="ml-2 text-xs text-muted-foreground">({t('invoice.voided')})</span>
            )}
          </>
        );
      },
    },
    {
      id: 'partner',
      header: t('table.partner'),
      cell: ({ row }) => {
        const invoice = row.original;
        return (
          <>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {invoice.partner?.name ?? '-'}
            </p>
            {invoice.partner?.taxNumber && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {invoice.partner.taxNumber}
              </p>
            )}
          </>
        );
      },
    },
    {
      id: 'type',
      header: t('common.type'),
      cell: ({ row }) => getTypeBadge(row.original.type),
    },
    {
      id: 'issueDate',
      header: t('invoicePage.issueDate'),
      cell: ({ row }) => (
        <span className="text-gray-600 dark:text-gray-300">
          {formatDate(row.original.invoiceDate)}
        </span>
      ),
    },
    {
      id: 'dueDate',
      header: t('invoicePage.dueDate'),
      cell: ({ row }) => (
        <span
          className={
            row.original.status === 'OVERDUE'
              ? 'text-red-600 font-medium dark:text-red-400'
              : 'text-gray-600 dark:text-gray-300'
          }
        >
          {formatDate(row.original.dueDate)}
        </span>
      ),
    },
    {
      id: 'amount',
      header: () => <div className="text-right">{t('invoicePage.amount')}</div>,
      cell: ({ row }) => {
        const invoice = row.original;
        const balanceDue = Number(invoice.grossAmount) - Number(invoice.paidAmount);
        return (
          <div className="text-right">
            <span
              className={`font-medium ${Number(invoice.grossAmount) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}
            >
              {formatPrice(Number(invoice.grossAmount))}
            </span>
            {balanceDue > 0 && invoice.status !== 'DRAFT' && (
              <p className="text-xs text-orange-600 dark:text-orange-400">
                {t('invoicePage.arrears')}: {formatPrice(balanceDue)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: () => <div className="text-center">{t('table.status')}</div>,
      cell: ({ row }) => <div className="text-center">{getStatusBadge(row.original.status)}</div>,
    },
    {
      id: 'dunning',
      header: () => <div className="text-center">{t('dunning.title')}</div>,
      cell: ({ row }) => <div className="text-center">{getDunningBadge(row.original.id)}</div>,
    },
    {
      id: 'nav',
      header: () => <div className="text-center">NAV</div>,
      cell: ({ row }) => (
        <div className="text-center">{getNavStatusBadge(row.original.navStatus)}</div>
      ),
    },
  ];

  // Calculate display stats from stats hook or fallback to invoices
  const displayStats = useMemo(() => {
    if (stats) {
      return stats;
    }
    // Fallback: calculate from current invoices
    const safeInvoices = invoices ?? [];
    return {
      total: safeInvoices.length,
      draft: safeInvoices.filter(i => i.status === 'DRAFT').length,
      sent: safeInvoices.filter(i => i.status === 'SENT').length,
      paid: safeInvoices.filter(i => i.status === 'PAID').length,
      overdue: safeInvoices.filter(i => i.status === 'OVERDUE').length,
      totalRevenue: safeInvoices
        .filter(i => i.status === 'PAID')
        .reduce((sum, i) => sum + Number(i.grossAmount), 0),
      unpaidTotal: safeInvoices
        .filter(i => i.status === 'SENT' || i.status === 'OVERDUE' || i.status === 'PARTIALLY_PAID')
        .reduce((sum, i) => sum + (Number(i.grossAmount) - Number(i.paidAmount)), 0),
    };
  }, [stats, invoices]);

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('invoice.title')}
              </h1>
              <p className="text-gray-500 dark:text-gray-400">{t('invoice.subtitle')}</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                aria-label={t('invoice.export.monthLabel')}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
                value={exportMonth}
                onChange={e => setExportMonth(e.target.value)}
              >
                {exportMonthOptions.map(month => (
                  <option key={month} value={month}>
                    {formatMonthLabel(month)}
                  </option>
                ))}
              </select>
              <Button
                onClick={() => void handleExport('xlsx')}
                loading={exportingFormat === 'xlsx'}
                disabled={exportingFormat !== null}
              >
                {t('invoice.export.xlsxButton')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleExport('csv')}
                loading={exportingFormat === 'csv'}
                disabled={exportingFormat !== null}
              >
                {t('invoice.export.csvButton')}
              </Button>
              <Button
                onClick={() => navigate('/invoices/new')}
                className="bg-primary hover:bg-primary/90"
              >
                {t('invoice.newInvoice')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('invoice.allInvoices')}
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {displayStats.total}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-sm text-green-600 dark:text-green-300">
                  {t('invoice.paidRevenue')}
                </p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-200">
                  {formatPrice(displayStats.totalRevenue)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-sm text-blue-600 dark:text-blue-300">
                  {t('invoice.issuedPending')}
                </p>
                <p className="text-3xl font-bold text-blue-700 dark:text-blue-200">
                  {displayStats.sent}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-sm text-red-600 dark:text-red-300">{t('invoice.overdueDebt')}</p>
                <p className="text-2xl font-bold text-red-700 dark:text-red-200">
                  {formatPrice(displayStats.unpaidTotal)}
                </p>
                <p className="text-xs text-red-500 dark:text-red-400">
                  {t('invoicePage.invoiceCount', { count: displayStats.overdue })}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <Input
                  type="search"
                  placeholder={t('invoice.searchPlaceholder')}
                  value={filters.search}
                  onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                />
              </div>
              <select
                className="rounded-md border border-gray-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
                value={filters.status}
                onChange={e =>
                  setFilters(f => ({ ...f, status: e.target.value as InvoiceStatus | 'ALL' }))
                }
              >
                <option value="ALL">{t('page.allStatuses')}</option>
                {INVOICE_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>
                    {t(s.labelKey)}
                  </option>
                ))}
              </select>
              <select
                className="rounded-md border border-gray-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
                value={filters.type}
                onChange={e =>
                  setFilters(f => ({ ...f, type: e.target.value as InvoiceType | 'ALL' }))
                }
              >
                <option value="ALL">{t('page.allTypes')}</option>
                {INVOICE_TYPES.map(tp => (
                  <option key={tp.value} value={tp.value}>
                    {t(tp.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Loading state */}
        {isLoading && (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-gray-500 dark:text-gray-400">
                {t('invoicePage.loadingInvoices')}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <CardContent className="py-4">
              <div className="text-center text-red-600 dark:text-red-400">
                {t('invoicePage.errorOccurred')}: {error}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoice List (megosztott DataTable foundation) */}
        {!isLoading && !error && (
          <Card>
            <CardContent className="p-0">
              <DataTable
                columns={columns}
                data={invoices ?? []}
                emptyMessage={t('invoicePage.noResults')}
                getRowId={invoice => invoice.id}
                onRowClick={invoice => navigate(`/invoices/${invoice.id}`)}
                {...(typeof total === 'number' ? serverPagination.tableProps(total) : {})}
                rowActions={invoice => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                  >
                    &rarr;
                  </Button>
                )}
              />
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
